(() => {
  'use strict';

  if (window.__OLM_SEARCH_V62_PATCH__) return;
  window.__OLM_SEARCH_V62_PATCH__ = true;

  const DEBOUNCE_MS = 220;
  const SEARCH_PAGE_KEY = 'olmerys:pending-search-v62';

  const FILTER_RULES = {
    all: [],
    savons: ['savon', 'savons'],
    visage: ['visage', 'serum', 'sérum', 'demaquillant', 'démaquillant', 'contour des yeux', 'creme visage', 'crème visage', 'anti-age', 'anti-âge'],
    corps: ['corps', 'douche', 'deodorant', 'déodorant', 'lait hydratant'],
    cheveux: ['cheveux', 'shampoing', 'shampooing', 'capillaire']
  };

  const normalize = (value = '') =>
    value.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

  const debounce = (fn, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  };

  /*
   * Shopify accepte les requêtes préfixes avec *.
   * Exemples : c* / cr* / cre* / savon*.
   * Cela permet d'obtenir des résultats dès le premier caractère.
   */
  const buildPrefixQuery = (term) => {
    return term
      .trim()
      .split(/\s+/)
      .map((token) => token.replace(/[\\:()\"]/g, '').trim())
      .filter(Boolean)
      .map((token) => `${token}*`)
      .join(' ');
  };

  class OlmSearchV62 {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-olm-search-form]');
      this.input = root.querySelector('[data-olm-search-input]');
      this.live = root.querySelector('[data-olm-full-results]');
      this.initial = root.querySelector('[data-olm-search-initial]');
      this.loading = root.querySelector('[data-olm-search-loading]');
      this.searchUrl = root.dataset.searchUrl || '/search';
      this.abortController = null;
      this.category = 'all';
      this.runDebounced = debounce(() => this.search(), DEBOUNCE_MS);
      this.bind();
    }

    bind() {
      if (!this.input || !this.form || !this.live) return;

      this.input.addEventListener('input', (event) => {
        event.stopPropagation();
        const term = this.input.value.trim();

        if (!term) {
          this.reset();
          return;
        }

        this.setLoading(true);
        this.runDebounced();
      });

      this.input.addEventListener('keydown', (event) => {
        event.stopPropagation();
        if (event.key === 'Enter') {
          event.preventDefault();
          if (this.input.value.trim()) this.search();
        }
      });

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.input.value.trim()) this.search();
      });

      this.root.addEventListener('click', (event) => this.onClick(event));
    }

    onClick(event) {
      const trend = event.target.closest('[data-olm-search-term]');
      if (trend) {
        event.preventDefault();
        event.stopPropagation();
        this.input.value = trend.dataset.olmSearchTerm || '';
        this.input.focus();
        this.search();
        return;
      }

      const filter = event.target.closest('[data-olm-category-filter]');
      if (filter) {
        event.preventDefault();
        this.category = filter.dataset.olmCategoryFilter || 'all';
        this.syncFilterButtons();
        this.applyFilters();
        return;
      }

      if (event.target.closest('[data-olm-filter-reset]')) {
        event.preventDefault();
        this.category = 'all';
        this.syncFilterButtons();
        this.applyFilters();
        return;
      }

      if (event.target.closest('[data-olm-filter-open]')) {
        event.preventDefault();
        const drawer = this.root.querySelector('[data-olm-filter-drawer]');
        if (drawer) {
          drawer.classList.add('is-open');
          drawer.setAttribute('aria-hidden', 'false');
        }
        return;
      }

      if (event.target.closest('[data-olm-filter-close]')) {
        event.preventDefault();
        const drawer = this.root.querySelector('[data-olm-filter-drawer]');
        if (drawer) {
          drawer.classList.remove('is-open');
          drawer.setAttribute('aria-hidden', 'true');
        }
      }
    }

    async search() {
      const visibleTerm = this.input.value.trim();
      if (!visibleTerm) {
        this.reset();
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.setLoading(true);

      try {
        const query = buildPrefixQuery(visibleTerm);
        const first = await this.fetchPage(query, 1);
        const pageNode = first.doc.querySelector('[data-olm-full-search-page]');
        const firstChunk = first.doc.querySelector('[data-olm-search-chunk]');
        const shellTemplate = first.doc.querySelector('[data-olm-active-search-shell]');

        if (!pageNode || !firstChunk || !shellTemplate) {
          throw new Error('Le template search.olmerys-live n’a pas été trouvé.');
        }

        const totalPages = Number(firstChunk.dataset.totalPages || 1);
        const allCards = [
          ...first.doc.querySelectorAll('[data-olm-search-cards] [data-olm-result-card]')
        ];

        for (let page = 2; page <= totalPages; page += 1) {
          const next = await this.fetchPage(query, page);
          allCards.push(
            ...next.doc.querySelectorAll('[data-olm-search-cards] [data-olm-result-card]')
          );
        }

        const shell = shellTemplate.content.cloneNode(true);
        this.live.innerHTML = '';
        this.live.appendChild(shell);

        const target = this.live.querySelector('[data-olm-active-products]');
        if (!target) throw new Error('Zone produits introuvable.');

        const fragment = document.createDocumentFragment();
        allCards.forEach((card) => fragment.appendChild(document.importNode(card, true)));
        target.appendChild(fragment);

        if (this.initial) this.initial.hidden = true;
        this.category = 'all';
        this.syncFilterButtons();
        this.applyFilters();
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error('[Olmérys Search V62]', error);
        this.live.innerHTML = `
          <div class="olm-search-empty">
            <strong>Impossible de charger la recherche.</strong>
            <span>Réessayez dans quelques secondes.</span>
          </div>
        `;
      } finally {
        this.setLoading(false);
      }
    }

    async fetchPage(query, page) {
      const url = new URL(this.searchUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('options[unavailable_products]', 'last');
      url.searchParams.set('options[fields]', 'title,tag,vendor,product_type,variants.title');
      url.searchParams.set('view', 'olmerys-live');
      url.searchParams.set('page', String(page));

      const response = await fetch(url.toString(), {
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: this.abortController.signal
      });

      if (!response.ok) throw new Error(`Recherche HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return { doc, url };
    }

    applyFilters() {
      const cards = [...this.live.querySelectorAll('[data-olm-result-card]')];
      const rules = (FILTER_RULES[this.category] || []).map(normalize);
      let visible = 0;

      cards.forEach((card) => {
        const haystack = normalize([
          card.dataset.title || '',
          card.dataset.productType || '',
          card.dataset.vendor || '',
          card.dataset.tags || ''
        ].join(' '));

        const show = this.category === 'all' || rules.some((keyword) => haystack.includes(keyword));
        card.hidden = !show;
        if (show) visible += 1;
      });

      this.live.querySelectorAll('[data-olm-visible-count]').forEach((node) => {
        node.textContent = String(visible);
      });
    }

    syncFilterButtons() {
      this.root.querySelectorAll('[data-olm-category-filter]').forEach((button) => {
        button.classList.toggle('is-active', button.dataset.olmCategoryFilter === this.category);
      });
    }

    setLoading(active) {
      if (this.loading) this.loading.hidden = !active;
    }

    reset() {
      if (this.abortController) this.abortController.abort();
      this.live.innerHTML = '';
      this.category = 'all';
      if (this.initial) this.initial.hidden = false;
      this.setLoading(false);
    }
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-olm-search-root]').forEach((root) => {
      if (root.dataset.olmSearchV62PatchReady === 'true') return;
      root.dataset.olmSearchV62PatchReady = 'true';
      root.__olmSearchV62 = new OlmSearchV62(root);
    });
  }

  function blockVisibleSearchPage() {
    if (window.Shopify?.designMode) return;
    if (window.location.pathname !== '/search') return;
    if (new URLSearchParams(window.location.search).get('view') === 'olmerys-live') return;

    const term = new URLSearchParams(window.location.search).get('q') || '';
    try { sessionStorage.setItem(SEARCH_PAGE_KEY, term); } catch (_) {}
    window.location.replace('/');
  }

  blockVisibleSearchPage();

  function boot() {
    init(document);

    let pending = null;
    try {
      pending = sessionStorage.getItem(SEARCH_PAGE_KEY);
      if (pending !== null) sessionStorage.removeItem(SEARCH_PAGE_KEY);
    } catch (_) {}

    if (pending !== null) {
      setTimeout(() => {
        const toggle = document.querySelector('.top-search-toggle') || document.querySelector('[aria-label="Recherche"]');
        if (toggle) toggle.click();

        setTimeout(() => {
          const root = [...document.querySelectorAll('[data-olm-search-root]')].find((el) => {
            const rect = el.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
          });
          const instance = root?.__olmSearchV62;
          if (!instance) return;

          instance.input.value = pending;
          instance.input.focus();
          if (pending) instance.search();
        }, 100);
      }, 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
